import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Newspaper, RefreshCw, ChevronRight } from 'lucide-react';
import { api, type Chronicle as ChronicleData } from '../lib/api';
import { usePanelOpen } from '../lib/usePanelOpen';

/** Chronique « Quoi de neuf » (CHRON) : digest sourcé des grains récents. */
export function Chronicle() {
  const { t } = useTranslation();
  const [open, setOpen] = usePanelOpen('gnosis.panel.chronicle.v2', false);
  const [data, setData] = useState<ChronicleData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void api.proactive
      .chronicle()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const refresh = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setRefreshing(true);
    void api.proactive
      .chronicleRefresh()
      .then(setData)
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  if (!data || !data.digest) return null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="group rounded-2xl bg-surface p-4 ring-1 ring-border"
    >
      <summary className="mb-2 flex cursor-pointer list-none items-center gap-2">
        <ChevronRight className="h-4 w-4 shrink-0 text-muted transition-transform group-open:rotate-90" />
        <h3 className="flex flex-1 items-center gap-1.5 text-sm font-semibold">
          <Newspaper className="h-4 w-4 text-accent" /> {t('chronicle.title')}
        </h3>
        <button
          onClick={refresh}
          disabled={refreshing}
          title={t('chronicle.refresh')}
          aria-label={t('chronicle.refresh')}
          className="grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </summary>
      <div className="md text-sm leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.digest}</ReactMarkdown>
      </div>
      {data.sources.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">{t('explore.sources')}</p>
          {data.sources.map((s) => (
            <details key={s.n} className="group rounded-lg border border-border bg-surface-2 px-3 py-2">
              <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-accent/15 text-xs text-accent">
                  {s.n}
                </span>
                <span className="truncate">{s.title}</span>
              </summary>
              <p className="mt-2 text-sm text-muted">{s.excerpt}…</p>
            </details>
          ))}
        </div>
      )}
    </details>
  );
}
